import { TextLink } from '@cinch-labs/design-system'
import { SbBlokData } from '@storyblok/react'
import helpMeChooseFallbackImage from '../../components/assets/fallback/help-me-choose.png'
import heroFallbackImage from '../../components/assets/fallback/hero-image.png'
import partExFallbackImage from '../../components/assets/fallback/part-exchange.png'
import whyCinchFallbackImage from '../../components/assets/fallback/why-cinch.png'
import { HelpMeChooseProps } from '../../components/help-me-choose/help-me-choose'
import { HeroProps } from '../../components/hero-v2/hero'
import { PartExchangeSectionProps } from '../../components/part-ex/part-ex'
import { ValuePropositionCardProps } from '../../components/value-propositions/value-propositions'
import { WhyCinchProps } from '../../components/why-cinch/why-cinch'

export const fallbackHeroProps: HeroProps = {
  _uid: 'fallback-hero',
  storyFallback: true,
  component: 'HomeHeroV2',
  heading: 'Summer daily deals',
  isHeroPromotional: false,
  isMobileImageShown: true,
  starRating: 0,
  starRatingLabel: '',
  numberOfReviews: 0,
  totalStock: 0,
  subheading:
    '£250 - £1000 off selected cars, with 14 days to love it or your money back.',
  image: {
    _uid: 'hero-image',
    component: 'HomeImage',
    filename: heroFallbackImage,
    alt: 'Cinch hero fallback image',
  },
  heroLink: [
    {
      _uid: 'fallback-link',
      component: 'LinkWithText',
      label: 'Browse Cars',
      href: '/used-cars',
      variant: 'primary',
      link: {
        url: '/used-cars?tags=cars-on-offer',
        linktype: 'url',
        fieldtype: 'multilink',
        cached_url: '/used-cars?tags=cars-on-offer',
      },
      text: 'View cars on offer',
    },
  ],
  heroPartExchange: [
    {
      _uid: 'fallback-hero-part-exchange',
      component: 'HeroV2PartExchange',
      title: 'Get a free, instant part exchange quote',
      label: 'Enter your car registration',
      ctaText: 'Start your free valuation',
      placeholder: 'Eg. A20 FGG',
      partExTextList: [
        {
          _uid: '1',
          component: 'Text',
          text: 'Guaranteed price for your car',
        },
        {
          _uid: '2',
          component: 'Text',
          text: 'No hidden fees',
        },
        {
          _uid: '3',
          component: 'Text',
          text: 'We collect your part ex car',
        },
      ],
    },
  ],
  makes: [],
}

export const valuePropositionsFallBack: ValuePropositionCardProps[] = [
  {
    heading: 'cinch quality assured',
    body: (
      <p>
        All cars are{' '}
        <TextLink href="/car-inspection" key="car-inspection" target="_blank">
          carefully reconditioned
        </TextLink>{' '}
        and have a minimum of 6 months on the MOT.
      </p>
    ),
    icon: 'shield',
  },
  {
    heading: 'Caring for your car',
    body: (
      <p>
        Our cars come with a 90-day warranty with breakdown assistance. Add{' '}
        <TextLink href="/cinch-care" key="cinch-care" target="_blank">
          cinchCare
        </TextLink>{' '}
        for lifetime warranty, breakdown assistance and servicing from £34.99 a
        month.
      </p>
    ),
    icon: 'UnionShield',
  },
]

export const helpMeChooseFallback: HelpMeChooseProps = {
  title: 'Not sure what you are looking for?',
  heading: 'Let cinch help you choose',
  body: 'Try our nifty Help Me Choose tool. Answer a few simple questions and find your perfect match',
  ctaText: 'Help me choose',
  image: {
    _uid: 'help-me-choose',
    component: 'HomeImage',
    filename: helpMeChooseFallbackImage,
    alt: 'help-me-choose',
  },
}

export const whyCinchFallback: WhyCinchProps = {
  title: 'Why cinch?',
  image: {
    _uid: 'why-chinch',
    component: 'HomeImage',
    filename: whyCinchFallbackImage,
    alt: 'why-chinch',
  },
  cards: [],
  fallback: true,
}

export const partExFallback: PartExchangeSectionProps = {
  title: 'Part exchange?',
  subtitle: 'Get a free, instant part-ex quote.',
  label: 'Enter your car registration',
  placeholder: 'E.g. A20FGG',
  ctaText: 'Start your free valuation',
  image: {
    _uid: 'part-exchange',
    component: 'HomeImage',
    filename: partExFallbackImage,
    alt: 'part-exchange',
  },
  link: 'Find out more about part exchange',
}

export const carsCarouselFallback: SbBlokData = {
  _uid: '37a28243-f5a3-4521-968c-114c5ce9406b',
  title: 'Shop new cars',
  sortBy: 'FIRST_PUBLISHED_DATE_DESC',
  priceTo: '35000',
  component: 'CarsCarousel',
  priceFrom: '10000',
  disclaimer: 'Sold by our partner Marshall Motor Group',
  carsCondition: 'new',
  showMonthlyPrices: true,
  showOnlyCinchStock: false,
  financeRepresentativeExample: [
    {
      name: 'BYD Finance Representative Examples',
      created_at: '2025-11-06T15:55:06.076Z',
      published_at: '2025-11-24T15:24:54.691Z',
      updated_at: '2025-11-24T15:24:54.704Z',
      id: 109_588_095_323_666,
      uuid: 'ac7cc910-1443-457b-92d9-476cff208394',
      content: {
        _uid: '220cc4e3-fe2e-4c33-beaa-9ccf639ae6cd',
        component: 'FinanceRepresentativeExample',
        creditAmount: '27863.92',
        onTheRoadPrice: '31723.92',
        customerDeposit: '2800',
        retailerDiscount: '1591.08',
        testDriveVoucher: '',
        representativeApr: '3.5',
        annualMileageLimit: '6000',
        totalAmountPayable: '34778',
        optionToPurchaseFee: '',
        totalInterestAmount: '3054.08',
        optionalFinalPayment: '15318',
        monthlyPaymentsAmount: '325',
        fixedAnnualInterestRate: '1.8',
        numberOfMonthlyPayments: '49',
        excessMileageChargeInPence: '14.9',
        financeDepositContribution: '1060',
      },
      slug: 'byd-finance-representative-example',
      full_slug:
        'finance-representative-examples/byd-finance-representative-example',
      sort_by_date: null,
      position: 0,
      tag_list: [],
      is_startpage: false,
      parent_id: 109_587_927_405_211,
      meta_data: null,
      group_id: 'ec4c6a09-c3e5-4b37-a4cb-46be5ca63135',
      first_published_at: '2025-11-10T16:18:40.920Z',
      release_id: null,
      lang: 'default',
      path: null,
      alternates: [],
      default_full_slug: null,
      translated_slugs: null,
      _stopResolving: true,
    },
  ],
}

export const usedCarsCarouselTabFallback: SbBlokData = {
  _uid: 'used-cars-tab',
  component: 'CarsCarousel',
  sortBy: 'FIRST_PUBLISHED_DATE_DESC',
  priceTo: 20_000,
  priceFrom: 10_000,
  carsCondition: 'used',
  showMonthlyPrices: false,
  showOnlyCinchStock: false,
  financeRepresentativeExample: [],
}

export const newCarsCarouselTabFallback: SbBlokData = {
  _uid: 'new-cars-tab',
  component: 'CarsCarousel',
  sortBy: 'FIRST_PUBLISHED_DATE_DESC',
  disclaimer: 'Sold by our partner Marshall Motor Group',
  carsCondition: 'new',
  showMonthlyPrices: false,
  showOnlyCinchStock: false,
  financeRepresentativeExample: [],
}
